import type { Theme, SxProps } from '@mui/material/styles';

import { useRef, useCallback } from 'react';
import { useBoolean } from 'minimal-shared/hooks';
import { varAlpha, isActiveLink, isExternalLink } from 'minimal-shared/utils';

import Collapse from '@mui/material/Collapse';

import { paths } from 'src/routes/paths';
import { usePathname } from 'src/routes/hooks';

import { CONFIG } from 'src/global-config';
import { RADIUS } from 'src/theme/ui-tokens';

import {
  navSectionClasses,
  NavSectionVertical as NavSectionVerticalBase,
} from 'src/components/nav-section';

import { NavLi } from '../components';
import { NavItem } from './nav-mobile-item';


// ----------------------------------------------------------------------

// NavSectionVertical 은 아직 JS 라 props 가 전부 필수로 추론된다 — 실제 계약(전부 선택)으로 좁혀 쓴다 (로직 무변)
const NavSectionVertical = NavSectionVerticalBase as unknown as React.ComponentType<{
  data?: unknown;
  sx?: SxProps<Theme>;
  slotProps?: Record<string, unknown>;
}>;

export type NavMobileItemData = {
  title: string;
  path: string;
  icon?: React.ReactNode;
  children?: unknown;
  useDemoLogin?: boolean;
};

type NavListProps = React.ComponentProps<typeof NavLi> & {
  data: NavMobileItemData;
  onClose?: () => void;
  onDemoExploreClick?: () => void;
};

export function NavList({ data, sx, onClose, onDemoExploreClick, ...other }: NavListProps) {
  const pathname = usePathname();
  const navItemRef = useRef(null);

  // paths.docs 는 실제로 정의돼 있지 않다(런타임 undefined — includes 에 안 걸릴 뿐) — A번호 후보, 국소 좁힘 (로직 무변)
  const isNotRootOrDocs = !['/', (paths as { docs?: string }).docs].includes(pathname);
  const isNotComponentsPath = !pathname.startsWith(paths.components);
  const isOpenPath = !!data.children && isNotRootOrDocs && isNotComponentsPath;

  const isActive = isActiveLink(pathname, data.path, !!data.children);

  const { value: open, onToggle } = useBoolean(isOpenPath);

  const handleToggleMenu = useCallback(() => {
    if (data.children) {
      onToggle();
    }
  }, [data.children, onToggle]);

  const handleItemClick = useCallback(
    (e: React.MouseEvent) => {
      if (data.useDemoLogin) {
        e.preventDefault();
        onDemoExploreClick?.();
        return;
      }
      handleToggleMenu();
    },
    [data.useDemoLogin, onDemoExploreClick, handleToggleMenu]
  );

  const renderNavItem = () => (
    <NavItem
      ref={navItemRef}
      // slots
      path={data.path}
      icon={data.icon}
      title={data.title}
      // state
      open={open}
      active={isActive}
      // options
      hasChild={!!data.children}
      externalLink={isExternalLink(data.path)}
      // actions
      onClick={handleItemClick}
    />
  );

  const renderCollapse = () =>
    !!data.children && (
      <Collapse in={open}>
        <NavSectionVertical
          data={data.children}
          sx={{
            px: 1.5,
            '& .MuiList-root': {
              gap: 2,
            }
          }}
          slotProps={{
            rootItem: {
              sx: [
                (theme: Theme) => ({
                  minHeight: 36,
                  '&[aria-label="Dashboard"]': {
                    [`& .${navSectionClasses.item.title}`]: {
                      display: 'none',
                    },
                    height: 180,
                    borderRadius: RADIUS.md,
                    backgroundSize: 'auto 88%',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                    backgroundImage: `url(${CONFIG.assetsDir}/assets/illustrations/illustration-dashboard.webp)`,
                    border: `solid 1px ${varAlpha(theme.vars.palette.grey['500Channel'], 0.12)}`,
                  },
                }),
              ],
            },
          }}
        />
      </Collapse>
    );

  return (
    <NavLi sx={sx} {...other}>
      {renderNavItem()}
      {renderCollapse()}
    </NavLi>
  );
}
